'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import AffiliateLink from '@/components/common/AffiliateLink'
import { ConvMoney } from '@/components/common/CurrencyContext'
import { t } from '@/i18n/translations'

// Aggregated per-operator plan ledger. Data comes straight from Sanity
// (providerOffering → esimPlan): operator switcher, coverage/type/calls/payment
// filters and sortable columns. The operator logo and name link to the in-site
// provider page; each "Select" deep-links to the partner. Coverage is a column
// (one operator can carry several regional plans), the region cell reveals its
// member countries on hover, and the table collapses past LIMIT rows.
const toGb = (gb, unit) => (unit === 'MB' ? gb / 1024 : unit === 'TB' ? gb * 1024 : gb)
const validityDays = (v) => {
    const n = parseInt(String(v ?? '').replace(/[^\d]/g, ''), 10)
    return Number.isFinite(n) ? n : null
}
const COV_ORDER = ['local', 'regional', 'global']
const PAY_TARGETS = ['paypal', 'cryptocurrency', 'mobile_payments']
const LIMIT = 8

export default function EsimPlansLedger({ lang = 'ru', country = '', countryName: _countryName = '', offers = [], preset = null }) {
    const vp = t(lang, 'esimPlansLedger')
    const providerHref = (code) => `/${lang}/${country}/esim/${code}`

    const ops = useMemo(() => offers.filter(o => o?.code && o?.plans?.length), [offers])

    const [op, setOp] = useState('all')
    const [covFilter, setCovFilter] = useState('all')
    const [typeFilter, setTypeFilter] = useState('all')
    const [callsFilter, setCallsFilter] = useState('all')
    const [payFilter, setPayFilter] = useState('all')
    const [sortKey, setSortKey] = useState('gb')
    const [sortDir, setSortDir] = useState(1)
    const [showAll, setShowAll] = useState(false)
    const [filtersOpen, setFiltersOpen] = useState(false)

    // Switching operator resets filters (handled in selectOp, not an [op] effect,
    // so programmatic preset changes don't get clobbered); any control re-collapses.
    const selectOp = (code) => {
        setOp(code)
        setCovFilter('all'); setTypeFilter('all'); setCallsFilter('all'); setPayFilter('all')
    }
    useEffect(() => { setShowAll(false) }, [op, covFilter, typeFilter, callsFilter, payFilter])

    // Apply a scenario preset coming from the "Тарифы на любой случай" cards.
    useEffect(() => {
        if (!preset || !preset.n) return
        setOp('all')
        setCovFilter(preset.cov || 'all')
        setTypeFilter(preset.type || 'all')
        setCallsFilter(preset.calls || 'all')
        setPayFilter(preset.pay || 'all')
        setSortKey(preset.sortKey || 'gb')
        setSortDir(preset.sortDir ?? 1)
        setFiltersOpen(true)
    }, [preset])

    const tx = vp
    const covName = (c) => (c === 'local' ? tx.local : c === 'global' ? tx.global : tx.regional)
    const typeName = (t) => (t === 'capped' ? tx.capped : t === 'unlimited' ? tx.unlimited : t === 'payg' ? tx.payg : tx.fAll)
    const payName = (m) => (m === 'paypal' ? 'PayPal' : m === 'cryptocurrency' ? tx.crypto : 'Apple/Google Pay')

    // Filter options available for the current operator pool.
    const meta = useMemo(() => {
        const pool = op === 'all' ? ops : ops.filter(o => o.code === op)
        const ap = pool.flatMap(o => o.plans)
        return {
            cov: COV_ORDER.filter(c => ap.some(p => (p.coverage || 'local') === c)),
            types: {
                capped: ap.some(p => !p.unlimited && !p.payg),
                unlimited: ap.some(p => p.unlimited),
                payg: ap.some(p => p.payg),
            },
            hasCalls: ap.some(p => p.calls),
            pay: PAY_TARGETS.filter(m => pool.some(o => (o.paymentMethods || []).includes(m))),
        }
    }, [ops, op])

    const flatRows = useMemo(() => {
        const list = []
        ops.forEach((o) => {
            o.plans.forEach((p) => {
                const cov = p.coverage || 'local'
                const gbNorm = (p.unlimited || p.payg) ? Infinity : toGb(p.gb, p.unit)
                const priceNum = p.payg ? (Number.isFinite(p.esimPrice) ? p.esimPrice : 0) : p.price
                list.push({
                    op: o.name,
                    code: o.code,
                    logo: o.logo,
                    payg: p.payg,
                    unlimited: p.unlimited,
                    calls: p.calls,
                    gb: (p.unlimited || p.payg) ? Infinity : p.gb,
                    unit: p.unit,
                    gbNorm,
                    days: validityDays(p.validity) ?? Infinity,
                    price: priceNum,
                    currency: p.payg ? p.paygCurrency : p.currency,
                    mbPrice: p.mbPrice,
                    perGB: p.payg ? (Number.isFinite(p.mbPrice) ? p.mbPrice : Infinity) : (p.unlimited ? Infinity : priceNum / (gbNorm || 1)),
                    coverage: cov,
                    regionName: p.regionName,
                    regionCountries: p.regionCountries,
                    priceCheckedAt: p.priceCheckedAt,
                    paymentMethods: o.paymentMethods || [],
                    href: p.href,
                })
            })
        })
        return list
    }, [ops])

    const rows = useMemo(() => {
        const list = flatRows.filter((r) => {
            if (op !== 'all' && r.code !== op) return false
            if (payFilter !== 'all' && !r.paymentMethods.includes(payFilter)) return false
            if (covFilter !== 'all' && r.coverage !== covFilter) return false
            if (typeFilter === 'capped' && (r.unlimited || r.payg)) return false
            if (typeFilter === 'unlimited' && !r.unlimited) return false
            if (typeFilter === 'payg' && !r.payg) return false
            if (callsFilter === 'calls' && !r.calls) return false
            return true
        }).map((r) => ({ ...r }))

        // Mark the cheapest per-GB capped row as the value pick (skip unlimited/payg).
        let best = null
        list.forEach((r) => { if (!r.payg && !r.unlimited && Number.isFinite(r.perGB) && (!best || r.perGB < best.perGB)) best = r })
        if (best) best.rec = 1

        const k = sortKey
        list.sort((a, b) => {
            const x = a[k === 'gb' ? 'gbNorm' : k], y = b[k === 'gb' ? 'gbNorm' : k]
            if (k === 'op') return a.op < b.op ? -sortDir : a.op > b.op ? sortDir : 0
            if (x === y) return 0
            return (x < y ? -1 : 1) * sortDir
        })
        return list
    }, [flatRows, op, covFilter, typeFilter, callsFilter, payFilter, sortKey, sortDir])

    const recommendations = (() => {
        const capped = flatRows.filter((r) => !r.payg && !r.unlimited && Number.isFinite(r.price) && Number.isFinite(r.perGB))
        const universalPool = capped.filter((r) => r.coverage === 'local' && r.gbNorm >= 5 && r.gbNorm <= 20 && r.days >= 15 && r.days <= 30)
        const universal = [...(universalPool.length ? universalPool : capped)].sort((a, b) => {
            const score = (r) => Math.abs(r.gbNorm - 10) * 0.25 + Math.abs(r.days - 30) * 0.08 + r.perGB
            return score(a) - score(b)
        })[0]
        const value = [...capped].sort((a, b) => a.perGB - b.perGB || a.price - b.price)[0]
        const unlimited = flatRows.filter((r) => r.unlimited && Number.isFinite(r.price)).sort((a, b) => a.price - b.price)[0]
        return [
            universal && { key: 'universal', label: tx.recUniversal, row: universal },
            value && { key: 'value', label: tx.recValue, row: value },
            unlimited && { key: 'unlimited', label: tx.recUnlimited, row: unlimited },
        ].filter(Boolean)
    })()

    const toggleSort = (k) => {
        if (sortKey === k) setSortDir((d) => d * -1)
        else { setSortKey(k); setSortDir(1) }
    }

    const Head = ({ k, label }) => (
        <span data-sort={k} className={sortKey === k ? 'act' : ''} onClick={() => toggleSort(k)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(k) } }}>
            {label}<i className="ar">{sortKey === k ? (sortDir > 0 ? '▲' : '▼') : ''}</i>
        </span>
    )

    const dataLabel = (r) => (r.payg ? tx.payg : r.unlimited ? tx.unlimited : `${r.gb} ${r.unit === 'GB' ? tx.gbU : r.unit}`)
    const perCell = (r) => {
        if (r.payg) return Number.isFinite(r.mbPrice)
            ? <ConvMoney amount={r.mbPrice} currency={r.currency} suffix={` / ${tx.mbU}`} />
            : '—'
        return r.unlimited ? '—' : <ConvMoney amount={r.perGB} currency={r.currency} suffix={` / ${tx.gbU}`} />
    }
    const covCell = (r) => {
        if (r.coverage === 'local') return <span className="cov-loc">{tx.local}</span>
        const label = r.coverage === 'global' ? tx.global : (r.regionName || tx.regional)
        if (!r.regionCountries) return <span className="cov-loc">{label}</span>
        return (
            <span className="cov-reg" tabIndex={0}>
                {label}
                <span className="cov-tip" role="tooltip">{r.regionCountries}</span>
            </span>
        )
    }

    if (!ops.length) return null

    const showCov = meta.cov.length > 1
    const showType = [meta.types.capped, meta.types.unlimited, meta.types.payg].filter(Boolean).length > 1
    const showPay = op === 'all' && meta.pay.length > 0
    const showFilters = showCov || showType || meta.hasCalls || showPay
    const visible = showAll ? rows : rows.slice(0, LIMIT)
    const activeFilters = [
        covFilter !== 'all' && `${tx.coverage}: ${covName(covFilter)}`,
        typeFilter !== 'all' && `${tx.type}: ${typeName(typeFilter)}`,
        callsFilter !== 'all' && `${tx.callsL}: ${tx.withCalls}`,
        payFilter !== 'all' && `${tx.payL}: ${payName(payFilter)}`,
    ].filter(Boolean)
    const filterSummary = activeFilters.length ? activeFilters.join(' · ') : tx.filtersHint
    const formatCheckedAt = (value) => {
        if (!value) return tx.notProvided
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return tx.notProvided
        return new Intl.DateTimeFormat(lang === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date)
    }

    return (
        <>
            {recommendations.length > 0 && (
                <div className="esim-recs" aria-label={tx.recommendationsAria}>
                    {recommendations.map(({ key, label, row: r }) => (
                        <article className={`esim-rec esim-rec--${key}`} key={key}>
                            <div className="esim-rec-top">
                                <span className="esim-rec-label">{label}</span>
                                <Link className="esim-rec-provider" href={providerHref(r.code)}>
                                    {r.logo && <img src={r.logo} alt="" aria-hidden="true" />}
                                    <span>{r.op}</span>
                                </Link>
                            </div>
                            <div className="esim-rec-price">
                                <ConvMoney amount={r.price} currency={r.currency} />
                                <span>{dataLabel(r)} · {Number.isFinite(r.days) ? tx.daysN(r.days) : tx.notProvided}</span>
                            </div>
                            <dl className="esim-rec-facts">
                                <div><dt>{tx.perGB}</dt><dd>{r.unlimited ? tx.unlimited : <ConvMoney amount={r.perGB} currency={r.currency} suffix={` / ${tx.gbU}`} />}</dd></div>
                                <div><dt>{tx.checked}</dt><dd>{formatCheckedAt(r.priceCheckedAt)}</dd></div>
                            </dl>
                            {r.href && (
                                <AffiliateLink href={r.href} provider={r.code} vertical="esim" country={country} position={`recommendation_${key}`} price={`${r.price}`} className="esim-rec-cta">
                                    {tx.select}
                                </AffiliateLink>
                            )}
                        </article>
                    ))}
                </div>
            )}

            <section className={`gf-ledger-controls ${showFilters ? 'has-filters' : ''} ${filtersOpen ? 'filters-open' : ''}`} aria-label={vp.controlsAria}>
                <div className="gf-ops" role="group" aria-label={vp.operatorAria}>
                    <button type="button" className={`all ${op === 'all' ? 'on' : ''}`} onClick={() => selectOp('all')}>{tx.all}</button>
                    {ops.map((o) => (
                        <button type="button" key={o.code} className={op === o.code ? 'on' : ''} onClick={() => selectOp(o.code)}>{o.name}</button>
                    ))}
                </div>

                {showFilters && (
                    <section className={`gf-filter-spoiler ${filtersOpen ? 'open' : ''}`} aria-label={tx.filters}>
                        <button
                            type="button"
                            className="gf-filter-toggle"
                            aria-expanded={filtersOpen}
                            onClick={() => setFiltersOpen((open) => !open)}
                        >
                            <span className="gf-filter-copy">
                                <span className="gf-filter-title">{tx.filters}</span>
                                <span className="gf-filter-state">{filterSummary}</span>
                            </span>
                            <span className="gf-filter-icon" aria-hidden="true">{filtersOpen ? '−' : '+'}</span>
                        </button>
                        {filtersOpen && (
                            <div className="gf-filters">
                                {showCov && (
                                    <div className="gf-fg">
                                        <span className="gf-fl">{tx.coverage}</span>
                                        <div className="gf-chips">
                                            <button type="button" className={covFilter === 'all' ? 'on' : ''} onClick={() => setCovFilter('all')}>{tx.fAll}</button>
                                            {meta.cov.map((c) => (
                                                <button type="button" key={c} className={covFilter === c ? 'on' : ''} onClick={() => setCovFilter(c)}>{covName(c)}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {showType && (
                                    <div className="gf-fg">
                                        <span className="gf-fl">{tx.type}</span>
                                        <div className="gf-chips">
                                            <button type="button" className={typeFilter === 'all' ? 'on' : ''} onClick={() => setTypeFilter('all')}>{tx.fAll}</button>
                                            {meta.types.capped && <button type="button" className={typeFilter === 'capped' ? 'on' : ''} onClick={() => setTypeFilter('capped')}>{tx.capped}</button>}
                                            {meta.types.unlimited && <button type="button" className={typeFilter === 'unlimited' ? 'on' : ''} onClick={() => setTypeFilter('unlimited')}>{tx.unlimited}</button>}
                                            {meta.types.payg && <button type="button" className={typeFilter === 'payg' ? 'on' : ''} onClick={() => setTypeFilter('payg')}>{tx.payg}</button>}
                                        </div>
                                    </div>
                                )}
                                {meta.hasCalls && (
                                    <div className="gf-fg">
                                        <span className="gf-fl">{tx.callsL}</span>
                                        <div className="gf-chips">
                                            <button type="button" className={callsFilter === 'all' ? 'on' : ''} onClick={() => setCallsFilter('all')}>{tx.fAll}</button>
                                            <button type="button" className={callsFilter === 'calls' ? 'on' : ''} onClick={() => setCallsFilter('calls')}>{tx.withCalls}</button>
                                        </div>
                                    </div>
                                )}
                                {showPay && (
                                    <div className="gf-fg">
                                        <span className="gf-fl">{tx.payL}</span>
                                        <div className="gf-chips">
                                            <button type="button" className={payFilter === 'all' ? 'on' : ''} onClick={() => setPayFilter('all')}>{tx.fAll}</button>
                                            {meta.pay.map((m) => (
                                                <button type="button" key={m} className={payFilter === m ? 'on' : ''} onClick={() => setPayFilter(m)}>{payName(m)}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                )}
            </section>

            <div className="gf-plans gf-plans-esim">
                <div className="gf-plan-row head">
                    <Head k="gb" label={tx.data} />
                    <Head k="days" label={tx.days} />
                    <span>{tx.coverage}</span>
                    <Head k="price" label={tx.price} />
                    <Head k="perGB" label={tx.perGB} />
                    {/* invisible clone of the row select link so the header's last
                        (auto) track reserves the same width as data rows — keeps the
                        fr columns aligned under their headings */}
                    <span className="sel" aria-hidden="true" style={{ visibility: 'hidden' }}>{tx.select}</span>
                </div>
                {visible.map((r, i) => (
                    <div className={`gf-plan-row ${r.rec ? 'rec' : ''}`} key={`${r.code}-${i}`}>
                        <span className="d">
                            <Link className="op-logo-link" href={providerHref(r.code)} aria-label={r.op}>
                                <img className="op-logo" src={r.logo} alt={r.op} loading="lazy"
                                    onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = '1'; e.currentTarget.src = '/images/providers/default.svg' } }} />
                            </Link>
                            <span className="d-tx">
                                <Link className="op-tag" href={providerHref(r.code)}>{r.op}</Link>
                                <span className="gbline">
                                    {dataLabel(r)}
                                    {r.calls ? <span className="badge-calls" title={vp.callsTitle}>{vp.callsBadge}</span> : null}
                                    {r.rec ? <span className="recl">{tx.best}</span> : null}
                                </span>
                            </span>
                        </span>
                        <span className="v">{Number.isFinite(r.days) ? tx.daysN(r.days) : '—'}</span>
                        <span className="cov">{covCell(r)}</span>
                        <ConvMoney className="p" amount={r.price} currency={r.currency} />
                        <span className="per">{perCell(r)}</span>
                        {r.href ? (
                            <AffiliateLink href={r.href} provider={r.code} vertical="esim" country={country} position="ledger_row" price={`${r.price}`} className="sel">
                                {tx.select}
                            </AffiliateLink>
                        ) : <span className="sel" aria-hidden="true" />}
                    </div>
                ))}
                {!rows.length && <p className="gf-plan-empty">{tx.empty}</p>}
            </div>

            {rows.length > LIMIT && (
                <button type="button" className="gf-showall" onClick={() => setShowAll(v => !v)}>
                    {showAll ? tx.collapse : tx.showAll(rows.length)}
                </button>
            )}
        </>
    )
}
